import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    Category,
    CategorySearchFilterOptions,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * API Client Service for Category entities.
 * Implements IFullAccessInternalApiClientService providing full CRUD access including
 * bulk operations for the Category model via the Internal API v2.
 *
 * Provides a clean abstraction for CRUD operations on product categories,
 * delegating HTTP communication to InternalApiRequestService while encapsulating
 * Category-specific request/response handling.
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<Category>}
 */
@Injectable({ providedIn: "root" })
export class CategoryApiClientService implements IFullAccessInternalApiClientService<Category> {

    readonly basePath = "category";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve multiple Category entities by their IDs in a single bulk request.
     *
     * @param ids Array of unique identifiers to retrieve
     * @returns Observable emitting a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Category>(id, Category.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a single Category by its unique identifier.
     *
     * @param id The unique identifier of the entity to retrieve
     * @returns Observable emitting the Category entity
     */
    get$(id: string): Observable<Category> {

        const request = RequestUtil.createGetRequest<void>(id, Category.modelName, ModelAction.ClientGet);

        return this._internalApiRequestService.get$<Category>(request, this.basePath).pipe(
            map((response: GetResponse<Category>) => response.payload)
        );
    }

    /**
     * Retrieve all Category entities matching the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting an array of all matching entities
     */
    getAll$(filterOptions: CategorySearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Category>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Category.modelName,
            undefined,
            undefined,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.getAll$<Category, CategorySearchFilterOptions>(request, this.basePath);
    }

    /**
     * Search for Category entities with pagination support.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting search results with pagination metadata
     */
    search$(
        filterOptions: CategorySearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Category>> {

        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Category.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$<Category, CategorySearchFilterOptions>(request, this.basePath);
    }

    /**
     * Create a new Category entity.
     *
     * @param fields The entity data to create
     * @returns Observable emitting the created entity
     */
    create$(fields: Category): Observable<Category> {

        const createRequest = RequestUtil.createCreateRequest<Category, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Create multiple Category entities in a single bulk request.
     *
     * @param items Array of create requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkCreate$(items: Array<CreateRequest<Category, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update an existing Category entity.
     *
     * @param id The unique identifier of the entity to update
     * @param version The current version for optimistic locking
     * @param fields The entity fields to update
     * @param removeFields Array of field names to remove (optional)
     * @returns Observable emitting the updated entity
     */
    update$(id: string, version: number, fields: Category, removeFields?: Array<string>): Observable<Category> {

        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Category> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Update a Category with changes computed from original and updated models.
     *
     * @param original The original entity
     * @param updated The updated entity with changes applied
     * @returns Observable emitting the updated entity
     */
    updateWithChanges$(original: Category, updated: Category): Observable<Category> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Update multiple Category entities in a single bulk request.
     *
     * @param requests Array of update requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a Category entity by ID and version.
     *
     * @param id The unique identifier of the entity to delete
     * @param version The current version for optimistic locking
     * @returns Observable emitting the delete response
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Category>(
            id, Category.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Delete multiple Category entities in a single bulk request.
     *
     * @param items Array of delete requests
     * @returns Observable emitting a BulkResponse with the results
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
